import style from './VideoUploadInputPopover.module.scss';
import classnames from 'classnames/bind';
import { VIDEO_TYPE, VideoType } from '../../../../interface/common/Video';
import Tab from '../../../tab/Tab';
import { FC, useState, useRef } from 'react';
import { BlockFormatType, EditorAlign } from '../../textEditor.type';
import TextFieldBase from '../../../textfield/TextFieldBase';
import Button from '../../../button/Button';
import { ReactEditor, useSlate } from 'slate-react';
import { Transforms } from 'slate';
import { postFileUpLoadV1 } from 'ats-editor/src/api/common/file';
import useToast from '../../../../context/toast/useToast';
import { getInitialEditorText } from '../../textEditor.constant';
const cx = classnames.bind(style);

const VideoTypeOptions: Array<{ value: VideoType; label: string }> = [
  {
    value: VIDEO_TYPE.YOUTUBE,
    label: '링크',
  },
  {
    value: VIDEO_TYPE.FILE,
    label: '파일 첨부',
  },
];

const MB_UNIT = 1024 * 1024; // 1 MB
const MAX_VIDEO_SIZE = 50;

const ALLOWED_VIDEO_FORMATS = ['video/mp4', 'video/quicktime'];

const VideoUploadInputPopover: FC<{ format: BlockFormatType; close: () => void }> = ({ format, close }) => {
  const editor = useSlate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [videoType, setVideoType] = useState<VideoType>(VIDEO_TYPE.YOUTUBE);
  const [youtubeLink, setYoutubeLink] = useState<string>('');
  const { setToast, setFailToast } = useToast();

  const changeVideoType = (type: VideoType) => {
    setVideoType(type);
  };

  const insertVideo = (videoData: { videoType: VideoType; videoUrl?: string }) => {
    const videoElement = {
      type: format,
      videoType: videoData.videoType,
      videoUrl: videoData.videoUrl,
      videoStyle: {
        borderRadius: 0,
        align: 'left' as EditorAlign,
      },
      children: [getInitialEditorText()],
    };

    Transforms.insertNodes(editor, videoElement);
    setToast({
      icon: 'POSITIVE',
      text: '동영상이 삽입되었습니다.',
    });
  };

  const insertText = () => {
    Transforms.insertNodes(editor, {
      type: 'paragraph',
      children: [getInitialEditorText()],
    });
  };

  const normalizeYoutubeUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const videoId = urlObj.searchParams.get('v');

      if (videoId) {
        return `https://www.youtube.com/watch?v=${videoId}`;
      }

      return url;
    } catch {
      return url;
    }
  };

  const handleYoutubeSave = () => {
    if (youtubeLink.trim()) {
      const normalizedUrl = normalizeYoutubeUrl(youtubeLink);
      insertVideo({ videoType: VIDEO_TYPE.YOUTUBE, videoUrl: normalizedUrl });
      insertText();
      setYoutubeLink('');
      close();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_VIDEO_FORMATS.includes(file.type)) {
      setToast({ icon: 'FAILED', text: '지원하지 않는 비디오 형식입니다.' });
      return;
    }

    if (file.size > MAX_VIDEO_SIZE * MB_UNIT) {
      setToast({ icon: 'FAILED', text: `파일 크기는 ${MAX_VIDEO_SIZE}MB 이하여야 합니다.` });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const { fileUrl } = await postFileUpLoadV1(formData);

      ReactEditor.focus(editor);
      insertVideo({ videoType: VIDEO_TYPE.FILE, videoUrl: fileUrl });
      insertText();
      close();
    } catch (e) {
      setFailToast();
      console.error(e);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={cx('video-upload-input-popover')}>
      <Tab
        className={cx('video-type-tab')}
        onChange={(tab) => changeVideoType(tab)}
        type={'secondary'}
        options={VideoTypeOptions}
        value={videoType}
      />
      <div className={cx('video-type-content')}>
        {videoType === VIDEO_TYPE.YOUTUBE && (
          <div className={cx('youtube-content')}>
            <TextFieldBase
              className={cx('youtube-link-input')}
              size={'md'}
              placeholder={'https://www.youtube.com/'}
              value={youtubeLink}
              onChange={(e) => setYoutubeLink(e.target.value)}
            />
            <Button onClick={handleYoutubeSave}>저장</Button>
          </div>
        )}
        {videoType === VIDEO_TYPE.FILE && (
          <div className={cx('file-content')}>
            <input
              ref={fileInputRef}
              type={'file'}
              accept={'video/*'}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <Button className={cx('video-uploader-trigger')} onClick={() => fileInputRef.current?.click()}>
              동영상 선택
            </Button>
            <div className={cx('video-upload-tip')}>
              <p>동영상 형식 : {ALLOWED_VIDEO_FORMATS.map((format) => format.split('/')[1]).join(', ')}</p>
              <p>최대 {MAX_VIDEO_SIZE} MB</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUploadInputPopover;
